import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getErrorMessage } from '../services/api/client';
import { emitToast } from './toastEvents';

// Query key factory for consistent key generation
export const queryKeys = {
  all: ['sprout'] as const,
  
  // Auth
  auth: () => [...queryKeys.all, 'auth'] as const,
  authUser: () => [...queryKeys.auth(), 'user'] as const,
  
  // Organizations
  organizations: () => [...queryKeys.all, 'organizations'] as const,
  organization: (id: string) => [...queryKeys.organizations(), id] as const,
  organizationCurrent: () => [...queryKeys.organizations(), 'current'] as const,
  organizationStats: () => [...queryKeys.organizationCurrent(), 'stats'] as const,
  organizationFeatures: () => [...queryKeys.organizationCurrent(), 'features'] as const,
  organizationLimits: () => [...queryKeys.organizationCurrent(), 'limits'] as const,
  
  // Users
  users: () => [...queryKeys.all, 'users'] as const,
  user: (id: string) => [...queryKeys.users(), id] as const,
  userMe: () => [...queryKeys.users(), 'me'] as const,
  
  // Children
  children: (filters?: any) => [...queryKeys.all, 'children', filters] as const,
  child: (id: string) => [...queryKeys.children(), id] as const,
  childrenByClassroom: (classroomId: string) => [...queryKeys.children(), 'classroom', classroomId] as const,
  childrenByGuardian: (guardianId: string) => [...queryKeys.children(), 'guardian', guardianId] as const,
  
  // Roles & Permissions
  roles: () => [...queryKeys.all, 'roles'] as const,
  role: (id: string) => [...queryKeys.roles(), id] as const,
  permissions: () => [...queryKeys.all, 'permissions'] as const,
  userRoles: (userId: string) => [...queryKeys.users(), userId, 'roles'] as const,
  
  // Academic Structure
  academicYears: () => [...queryKeys.all, 'academic-years'] as const,
  academicYear: (id: string) => [...queryKeys.academicYears(), id] as const,
  grades: (yearId?: string) => [...queryKeys.all, 'grades', yearId] as const,
  grade: (id: string) => [...queryKeys.grades(), id] as const,
  classrooms: (gradeId?: string) => [...queryKeys.all, 'classrooms', gradeId] as const,
  classroom: (id: string) => [...queryKeys.classrooms(), id] as const,
  
  // Observations
  observations: (filters?: any) => [...queryKeys.all, 'observations', filters] as const,
  observation: (id: string) => [...queryKeys.observations(), id] as const,
  observationsByChild: (childId: string, filters?: any) => [...queryKeys.observations(), 'child', childId, filters] as const,
  myObservations: (filters?: any) => [...queryKeys.observations(), 'mine', filters] as const,
  recentObservations: (limit: number) => [...queryKeys.observations(), 'recent', limit] as const,
  milestoneObservations: (filters?: any) => [...queryKeys.observations(), 'milestones', filters] as const,
  concernObservations: (filters?: any) => [...queryKeys.observations(), 'concerns', filters] as const,
  observationsByCategory: (category: string, filters?: any) => [...queryKeys.observations(), 'category', category, filters] as const,
  
  // Observation Media
  observationMedia: (observationId?: string) => [...queryKeys.all, 'observation-media', observationId] as const,
  observationMediaItem: (mediaId: string) => [...queryKeys.observationMedia(), mediaId] as const,
  
  // Guardians
  guardians: () => [...queryKeys.all, 'guardians'] as const,
  guardian: (id: string) => [...queryKeys.guardians(), id] as const,
  guardianChildren: (guardianId: string) => [...queryKeys.guardians(), guardianId, 'children'] as const,
};

// Create query client with global error handling
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors except 408 (timeout)
          if (error instanceof AxiosError) {
            const status = error.response?.status;
            if (status && status >= 400 && status < 500 && status !== 408) {
              return false;
            }
          }
          return failureCount < 3;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false, // Don't retry mutations by default
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Global query error handler
        if (query.state.data !== undefined) {
          // Only show error if we don't have cached data
          return;
        }

        const message = getErrorMessage(error);
        console.error('Query error:', message);

        emitToast({
          type: 'error',
          title: 'Error',
          message,
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        // Global mutation error handler
        const message = getErrorMessage(error);
        console.error('Mutation error:', message);

        emitToast({
          type: 'error',
          title: 'Error',
          message,
        });
      },
    }),
  });
};

// Singleton instance
export const queryClient = createQueryClient();
